import { getCurrentInstance, onScopeDispose, Ref, watch } from 'vue';
import { useGtag } from '@/composition/use-gtag';
import { useUi } from '@/composition/use-ui';
import { chunkChanges } from '@/domain/catalog/bulk-chunks';
import {
  MENU_V2_EVENT,
  menuV2EventParams,
  TMenuV2EventLabel,
} from '@/domain/catalog/catalog-analytics';
import { AlertType, IAlert } from '@/interfaces/IAlert';
import { vueData } from '@/VueData';
import type { ICatalogPageState } from './catalog-page-state';

/** Toasts with an action («Deshacer», «Poner precio») stay this long (the default is 3 s). */
export const UNDO_TOAST_MS = 8000;

/** What an undo could not restore. */
export interface IUndoReport {
  /** Changed meanwhile by another device: left as they are. */
  kept: number;
  /** No longer exist. */
  skipped: number;
}

/** A bulk action's outcome: the products it changed (they leave a selection). */
export interface IBulkOutcome {
  appliedIds: number[];
}

/** A bulk write split in calls of 500 (`chunkChanges`), run one after another. */
export interface IChunkedWrite<TResult> {
  results: TResult[];
  /** The products the server changed (per its responses). */
  appliedIds: number[];
  /** A later call failed: the rest was not sent. */
  stopped: boolean;
}

export interface IToast {
  message: string;
  type: AlertType;
}

export interface ICatalogWritesDeps {
  state: ICatalogPageState;
  /** The current store; writes and undos never cross stores. */
  restaurantId: () => number | null;
  online: Ref<boolean>;
  /** Rows are pinned only while a filter or search could hide them. */
  isFiltering: () => boolean;
}

/**
 * The write kit of the Menú page (plan §5.9): offline and error handling,
 * bulk calls in chunks of 500, toasts with an action («Deshacer», withdrawn
 * when the store changes or the page closes, so it never writes into
 * another store), analytics and row pinning.
 */
export function useCatalogWrites(deps: ICatalogWritesDeps) {
  const { state, online } = deps;
  const ui = useUi();
  const gtag = useGtag();
  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  const tc = (key: string, count: number, params?: Record<string, unknown>) =>
    String(proxy.$tc(key, count, params));

  /** Toasts whose action must not outlive the store or the page. */
  const actionAlerts = new Set<IAlert>();

  function withdrawActions() {
    for (const alert of actionAlerts) ui.hideToast(alert);
    actionAlerts.clear();
  }
  watch(deps.restaurantId, withdrawActions);
  onScopeDispose(withdrawActions);

  function showOffline() {
    ui.showToast(AlertType.Error, t('menuPage.errors.offline'));
  }

  /** Runs a write; offline or failed writes tell the owner and return null. */
  async function guarded<T>(write: () => Promise<T>): Promise<T | null> {
    if (!online.value) {
      showOffline();
      return null;
    }
    try {
      return await write();
    } catch (error) {
      ui.serverError(error, t('menuPage.errors.save'));
      return null;
    }
  }

  /** A toast with one action, withdrawn on a store switch or when the page closes. */
  function showAction(
    toast: IToast,
    action: { text: string; run: () => void }
  ) {
    // Forget the toasts that already expired.
    for (const shown of actionAlerts)
      if (!vueData.alerts.includes(shown)) actionAlerts.delete(shown);
    const alert = ui.showToast(
      toast.type,
      toast.message,
      {
        text: action.text,
        cb: () => {
          actionAlerts.delete(alert);
          action.run();
        },
      },
      UNDO_TOAST_MS
    );
    actionAlerts.add(alert);
  }

  /** `undo` resolves null when it already told the owner it failed. */
  async function runUndo(undo: () => Promise<IUndoReport | null>) {
    const report = await guarded(undo);
    if (!report) return;
    if (!report.kept && !report.skipped) {
      ui.showToast(AlertType.Success, t('menuPage.toast.undone'));
      return;
    }
    // Part of it could not be undone: say what stayed as it was.
    const parts: string[] = [];
    if (report.kept)
      parts.push(tc('menuPage.toast.kept', report.kept, { n: report.kept }));
    if (report.skipped)
      parts.push(
        tc('menuPage.toast.skipped', report.skipped, { n: report.skipped })
      );
    ui.showToast(AlertType.Info, parts.join(' '));
  }

  /** The result toast, with «Deshacer» when there is something to undo. */
  function showResult(
    toast: IToast,
    undo: (() => Promise<IUndoReport | null>) | null
  ) {
    if (undo)
      showAction(toast, {
        text: t('menuPage.undo'),
        run: () => void runUndo(undo),
      });
    else ui.showToast(toast.type, toast.message);
  }

  /**
   * One bulk write per 500 changes (`chunkChanges`), in order. `applied`
   * reads the products each response changed (skipped ones no longer
   * exist). When a later call fails the earlier ones stay applied: the
   * caller reports it (`bulkToast`) and undoes only what was applied.
   */
  async function writeInChunks<TChange, TResult>(
    changes: readonly TChange[],
    write: (part: TChange[]) => Promise<TResult>,
    applied: (result: TResult) => readonly number[]
  ): Promise<IChunkedWrite<TResult> | null> {
    if (!online.value) {
      showOffline();
      return null;
    }
    const results: TResult[] = [];
    const appliedIds: number[] = [];
    for (const part of chunkChanges(changes)) {
      try {
        const result = await write(part);
        results.push(result);
        appliedIds.push(...applied(result));
      } catch (error) {
        if (!results.length) {
          ui.serverError(error, t('menuPage.errors.save'));
          return null;
        }
        console.error('Bulk write stopped', error);
        return { results, appliedIds, stopped: true };
      }
    }
    return { results, appliedIds, stopped: false };
  }

  /**
   * The same for an undo; `apply` stores each response as it arrives, so a
   * later failure (it throws; the undo reports it) keeps what was undone.
   */
  async function undoInChunks<TChange, TResult>(
    changes: readonly TChange[],
    write: (part: TChange[]) => Promise<TResult>,
    apply: (result: TResult) => void
  ): Promise<TResult[]> {
    const results: TResult[] = [];
    for (const part of chunkChanges(changes)) {
      const result = await write(part);
      apply(result);
      results.push(result);
    }
    return results;
  }

  /**
   * The toast of a bulk write: `success` for what the server changed, plus
   * «{n} productos ya no existen»; «Se aplicó a {x} de {y}…» when a call
   * failed.
   */
  function bulkToast(
    total: number,
    written: IChunkedWrite<unknown>,
    success: (applied: number) => string
  ): IToast {
    const applied = written.appliedIds.length;
    if (written.stopped)
      return {
        message: t('menuPage.toast.partial', { x: applied, y: total }),
        type: AlertType.Error,
      };
    const skipped = total - applied;
    if (!skipped) return { message: success(applied), type: AlertType.Success };
    const gone = tc('menuPage.toast.skipped', skipped, { n: skipped });
    return applied
      ? { message: `${success(applied)} ${gone}`, type: AlertType.Info }
      : { message: gone, type: AlertType.Error };
  }

  function track(label: TMenuV2EventLabel) {
    gtag.event(MENU_V2_EVENT, menuV2EventParams(label));
  }

  /** Acted-on rows stay listed while the filter or search no longer matches them. */
  function pin(ids: readonly number[]) {
    if (!deps.isFiltering()) return;
    const pinned = { ...state.pinnedIds };
    for (const id of ids) pinned[id] = true;
    state.pinnedIds = pinned;
  }

  return {
    restaurantId: deps.restaurantId,
    guarded,
    writeInChunks,
    undoInChunks,
    bulkToast,
    showResult,
    showAction,
    showOffline,
    track,
    pin,
  };
}

export type ICatalogWrites = ReturnType<typeof useCatalogWrites>;
